import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { supabase } from './supabaseClient'
import austinJonesPhoto from './assets/austin-jones.jpg'
import heroTarponPhoto from './assets/hero-tarpon.jpg'
import './Home.css'

// ─── DATA HELPER ─────────────────────────────────────────────────────────────
// Loads a tournament result from the derived views (scores come from catch data).
async function loadTournamentResult(year) {
  const [{ data: w }, { data: angler }, { data: guide }] = await Promise.all([
    supabase.from('year_winners').select('*').eq('year', year).maybeSingle(),
    supabase.from('top_anglers_by_year').select('name, score').eq('tournament_year', year).maybeSingle(),
    supabase.from('top_guides_by_year').select('name, score').eq('tournament_year', year).maybeSingle(),
  ])

  if (!w) return null

  return {
    year:             w.year,
    team_a_score:     w.team_a_score,
    team_b_score:     w.team_b_score,
    winning_team:     w.winning_team,
    top_angler_name:  angler?.name  ?? null,
    top_angler_score: angler?.score ?? null,
    top_guide_name:   guide?.name   ?? null,
    top_guide_score:  guide?.score  ?? null,
    notes:            null,
  }
}

// ─── NAV ─────────────────────────────────────────────────────────────────────
export function PublicNav() {
  const [menuOpen, setMenuOpen] = useState(false)
  const navRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return
    const onOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onOutside)
    document.addEventListener('touchstart', onOutside)
    return () => {
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('touchstart', onOutside)
    }
  }, [menuOpen])

  const close = () => setMenuOpen(false)

  return (
    <nav className="pub-nav" ref={navRef}>
      <Link to="/" className="pub-nav-brand" onClick={close}>
        <span className="pub-nav-anchor">⚓</span>
        <span className="pub-nav-title">The Tarpon Memorial</span>
      </Link>

      {/* Desktop links */}
      <div className="pub-nav-links">
        <Link to="/results" className="pub-nav-link">All Results</Link>
        <Link to="/records" className="pub-nav-link">Records</Link>
        <Link to="/gallery" className="pub-nav-link">Gallery</Link>
        <Link to="/about" className="pub-nav-link">About</Link>
        <Link to="/austin-jones" className="pub-nav-link">Austin Jones</Link>
        <Link to="/upload" className="pub-nav-link pub-nav-link-cta">Upload</Link>
      </div>

      {/* Hamburger button (mobile only) */}
      <button
        className={`pub-nav-hamburger${menuOpen ? ' pub-nav-hamburger-open' : ''}`}
        onClick={() => setMenuOpen(o => !o)}
        aria-label="Toggle navigation"
        aria-expanded={menuOpen}
      >
        <span /><span /><span />
      </button>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="pub-nav-mobile-menu">
          <Link to="/results"      className="pub-nav-mobile-link" onClick={close}>All Results</Link>
          <Link to="/records"      className="pub-nav-mobile-link" onClick={close}>Records</Link>
          <Link to="/gallery"      className="pub-nav-mobile-link" onClick={close}>Gallery</Link>
          <Link to="/about"        className="pub-nav-mobile-link" onClick={close}>About</Link>
          <Link to="/austin-jones" className="pub-nav-mobile-link" onClick={close}>Austin Jones</Link>
          <Link to="/upload"       className="pub-nav-mobile-link pub-nav-mobile-link-cta" onClick={close}>Upload</Link>
        </div>
      )}
    </nav>
  )
}

// ─── HOMEPAGE ────────────────────────────────────────────────────────────────
export function PublicHomepage() {
  const [settings, setSettings] = useState(null)
  const [latestResult, setLatestResult] = useState(null)
  const [loadingSettings, setLoadingSettings] = useState(true)
  const [loadingResult, setLoadingResult] = useState(true)

  useEffect(() => {
    supabase.from('tournament_settings').select('*').eq('id', 1).maybeSingle()
      .then(({ data }) => { setSettings(data); setLoadingSettings(false) })

    // year_winners only contains non-cancelled years with real scores
    supabase
      .from('year_winners')
      .select('year')
      .order('year', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(async ({ data }) => {
        if (data) {
          const result = await loadTournamentResult(data.year)
          setLatestResult(result)
        }
        setLoadingResult(false)
      })
  }, [])

  return (
    <div className="pub-home">
      <PublicNav />
      <HeroSection />
      <NextTournamentSection settings={settings} loading={loadingSettings} />
      <LatestResultsSection result={latestResult} loading={loadingResult} />
      <SponsorSection />
    </div>
  )
}

// ─── HERO ────────────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section className="pub-hero" style={{ backgroundImage: `url(${heroTarponPhoto})` }}>
      <div className="pub-hero-inner">

        {/* Left: tournament identity */}
        <div className="pub-hero-left">
          <div className="pub-hero-eyebrow">Islamorada · Florida Keys</div>
          <h1 className="pub-hero-title">The Tarpon<br />Memorial</h1>
          <p className="pub-hero-tagline">
            Celebrating the silver king and the bonds forged<br />
            in Florida's backcountry waters
          </p>
          <div className="pub-hero-divider" />
          <p className="pub-hero-sub">An annual tradition since 2009</p>
        </div>

        {/* Right: Austin Jones tribute */}
        <div className="pub-hero-right">
          <div className="pub-austin-tribute">
            <div className="pub-austin-in-memory">In Memory of Our Dear Friend</div>
            <div className="pub-austin-name">Austin Jones</div>
            <img src={austinJonesPhoto} alt="Austin Jones" className="pub-austin-photo" />
            <Link to="/austin-jones" className="pub-austin-story-btn">His Story →</Link>
          </div>
        </div>

      </div>
      <div className="pub-hero-wave">
        <svg viewBox="0 0 1440 72" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,36 C240,72 480,0 720,36 C960,72 1200,0 1440,36 L1440,72 L0,72 Z" fill="#faf6f0" />
        </svg>
      </div>
    </section>
  )
}

// ─── NEXT TOURNAMENT ─────────────────────────────────────────────────────────
function NextTournamentSection({ settings, loading }) {
  return (
    <section className="pub-next-section">
      <div className="pub-container">
        <div className="pub-next-banner">
          <span className="pub-next-eyebrow">Mark Your Calendar</span>
          {loading ? (
            <span className="pub-next-loading">Loading…</span>
          ) : settings ? (
            <>
              <span className="pub-next-date">{settings.next_date || 'Date TBD'}</span>
              <span className="pub-next-sep">·</span>
              <span className="pub-next-location">📍 {settings.next_location || 'Location TBD'}</span>
              {settings.notes && <span className="pub-next-notes">{settings.notes}</span>}
            </>
          ) : (
            <span className="pub-next-date">Details Coming Soon</span>
          )}
        </div>
      </div>
    </section>
  )
}

// ─── LATEST RESULTS ──────────────────────────────────────────────────────────
function LatestResultsSection({ result, loading }) {
  if (!loading && !result) return null

  return (
    <section className="pub-section pub-results-section">
      <div className="pub-container">
        <h2 className="pub-section-title">Latest Results</h2>
        {loading ? (
          <div className="pub-loading">Loading…</div>
        ) : (
          <ResultCard result={result} linkToFull />
        )}
      </div>
    </section>
  )
}

// ─── RESULT CARD (shared by homepage + results page) ─────────────────────────
export function ResultCard({ result, linkToFull }) {
  if (!result) return null

  const aWins = result.winning_team === 'Team A'
  const bWins = result.winning_team === 'Team B'

  return (
    <div className="pub-result-card">
      <div className="pub-result-year-badge">{result.year} Season</div>

      <div className="pub-result-score">
        <div className="pub-result-team">
          <div className="pub-result-team-name">Team A</div>
          <div className={`pub-result-team-score${aWins ? ' winner' : ''}`}>
            {result.team_a_score ?? '—'}
          </div>
        </div>
        <div className="pub-result-vs">vs</div>
        <div className="pub-result-team">
          <div className="pub-result-team-name">Team B</div>
          <div className={`pub-result-team-score${bWins ? ' winner' : ''}`}>
            {result.team_b_score ?? '—'}
          </div>
        </div>
      </div>

      {result.winning_team && (
        <div className="pub-result-winner">{result.winning_team} Wins</div>
      )}

      {(result.top_angler_name || result.top_guide_name) && (
        <div className="pub-result-stats">
          {result.top_angler_name && (
            <div className="pub-result-stat">
              <div className="pub-result-stat-label">Top Angler</div>
              <div className="pub-result-stat-name">{result.top_angler_name}</div>
              {result.top_angler_score != null && (
                <div className="pub-result-stat-score">{result.top_angler_score} pts</div>
              )}
            </div>
          )}
          {result.top_guide_name && (
            <div className="pub-result-stat">
              <div className="pub-result-stat-label">Top Guide</div>
              <div className="pub-result-stat-name">{result.top_guide_name}</div>
              {result.top_guide_score != null && (
                <div className="pub-result-stat-score">{result.top_guide_score} pts</div>
              )}
            </div>
          )}
        </div>
      )}

      {result.notes && (
        <p style={{ fontSize: '0.88rem', color: 'var(--fk-text-dim)', marginBottom: '1.25rem', lineHeight: 1.65 }}>
          {result.notes}
        </p>
      )}

      {linkToFull && (
        <Link to={`/results/${result.year}`} className="pub-result-link">
          Full {result.year} results →
        </Link>
      )}
    </div>
  )
}

// ─── SPONSOR ─────────────────────────────────────────────────────────────────
function SponsorSection() {
  return (
    <footer className="pub-sponsor-section">
      <div className="pub-container">
        <div className="pub-sponsor-divider" />
        <div className="pub-sponsors">
          <div className="pub-sponsor">
            <div className="pub-sponsor-presented">Sponsored by</div>
            <div className="pub-sponsor-name">
              <a href="https://www.regionfishing.com" target="_blank" rel="noopener noreferrer" className="pub-sponsor-link">RegionFishing.com</a>
            </div>
          </div>
        </div>
        <div className="pub-footer-copy">
          © {new Date().getFullYear()} The Tarpon Memorial · Islamorada, Florida Keys
        </div>
      </div>
    </footer>
  )
}

// ─── YEAR DETAIL PAGE (/results/:year) ───────────────────────────────────────
export function TournamentYearPage() {
  const { year } = useParams()
  const yearNum = parseInt(year, 10)

  const [loading, setLoading]           = useState(true)
  const [notFound, setNotFound]         = useState(false)
  const [tournament, setTournament]     = useState(null)
  const [winner, setWinner]             = useState(null)
  const [participants, setParticipants] = useState([])
  const [catches, setCatches]           = useState([])
  const [media, setMedia]               = useState([])
  const [allYears, setAllYears]         = useState([])
  const [lbPhoto, setLbPhoto]           = useState(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setNotFound(false)

      const [{ data: t }, { data: years }] = await Promise.all([
        supabase.from('tournaments').select('year, location, venue, cancelled').eq('year', yearNum).maybeSingle(),
        supabase.from('tournaments').select('year').order('year', { ascending: true }),
      ])

      if (!t) { setNotFound(true); setLoading(false); return }

      setAllYears((years || []).map(r => r.year))
      setTournament(t)

      if (t.cancelled) { setLoading(false); return }

      const [{ data: w }, { data: ps }, { data: cs }, { data: ms }] = await Promise.all([
        supabase.from('year_winners').select('*').eq('year', yearNum).maybeSingle(),
        supabase.from('tournament_participants')
          .select('role, score, team, participants(id, name)')
          .eq('tournament_year', yearNum),
        supabase.from('catches').select('species, points, catch_type, participants!inner(role)').eq('tournament_year', yearNum).eq('participants.role', 'angler'),
        supabase.from('media')
          .select('id, file_url, file_type, caption')
          .eq('tournament_year', yearNum)
          .eq('status', 'approved'),
      ])

      setWinner(w)
      setParticipants(ps || [])
      setCatches(cs || [])

      if (ms?.length) {
        const { data: signed } = await supabase.storage
          .from('tarpon-media')
          .createSignedUrls(ms.map(m => m.file_url), 3600)
        setMedia(ms.map((m, i) => ({ ...m, signedUrl: signed?.[i]?.signedUrl || null })))
      }

      setLoading(false)
    }
    load()
  }, [yearNum])

  // Escape key closes lightbox
  useEffect(() => {
    if (!lbPhoto) return
    const onKey = e => { if (e.key === 'Escape') setLbPhoto(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lbPhoto])

  if (loading) return (
    <div className="pub-results-page-wrap">
      <PublicNav />
      <div className="pub-year-body">
        <div className="pub-loading" style={{ padding: '4rem 0', textAlign: 'center' }}>Loading…</div>
      </div>
    </div>
  )

  if (notFound) return <Navigate to="/results" replace />

  const yearIndex = allYears.indexOf(yearNum)
  const prevYear  = yearIndex > 0                    ? allYears[yearIndex - 1] : null
  const nextYear  = yearIndex < allYears.length - 1  ? allYears[yearIndex + 1] : null

  const anglers = participants
    .filter(p => p.role === 'angler' && p.participants)
    .sort((a, b) => (b.score || 0) - (a.score || 0))

  const guides = participants
    .filter(p => p.role === 'guide' && p.participants)
    .sort((a, b) => (b.score || 0) - (a.score || 0))

  // Catch breakdown grouped by species
  const catchMap = {}
  for (const c of catches) {
    if (!catchMap[c.species]) catchMap[c.species] = { species: c.species, count: 0, pts_each: c.points, total: 0 }
    catchMap[c.species].count++
    catchMap[c.species].total += c.points
  }
  const catchRows = Object.values(catchMap).sort((a, b) => b.total - a.total)

  const photos = media.filter(m => m.file_type === 'photo' && m.signedUrl)

  return (
    <div className="pub-results-page-wrap">
      <PublicNav />
      <div className="pub-year-body">
        <div className="pub-year-container">

          {/* Prev / Next navigation */}
          <div className="pub-year-nav">
            {prevYear
              ? <Link to={`/results/${prevYear}`} className="pub-year-arrow">← {prevYear}</Link>
              : <span className="pub-year-arrow pub-year-arrow-ghost" />
            }
            <Link to="/results" className="pub-year-nav-all">All Results</Link>
            {nextYear
              ? <Link to={`/results/${nextYear}`} className="pub-year-arrow">{nextYear} →</Link>
              : <span className="pub-year-arrow pub-year-arrow-ghost" />
            }
          </div>

          {/* Header card */}
          <div className="pub-year-header">
            <div className="pub-year-title-row">
              <div className="pub-year-big">{tournament.year}</div>
              <div className="pub-year-location-block">
                <div className="pub-year-location">📍 {tournament.location || 'Islamorada, Florida Keys'}</div>
                {tournament.venue && <div className="pub-year-venue">{tournament.venue}</div>}
              </div>
            </div>

            {tournament.cancelled ? (
              <div className="pub-year-cancelled">
                <div className="pub-year-cancelled-title">Tournament Cancelled</div>
                <div className="pub-year-cancelled-sub">The {tournament.year} tournament did not take place.</div>
              </div>
            ) : winner ? (
              <>
                <div className="pub-year-score-row">
                  <div className="pub-year-team">
                    <div className="pub-year-team-label">Team A</div>
                    <div className={`pub-year-team-score${winner.winning_team === 'Team A' ? ' winner' : ''}`}>
                      {winner.team_a_score}
                    </div>
                  </div>
                  <div className="pub-year-vs">vs</div>
                  <div className="pub-year-team">
                    <div className="pub-year-team-label">Team B</div>
                    <div className={`pub-year-team-score${winner.winning_team === 'Team B' ? ' winner' : ''}`}>
                      {winner.team_b_score}
                    </div>
                  </div>
                </div>
                {winner.winning_team && (
                  <div className="pub-year-winner-banner">
                    {winner.winning_team} Wins · {winner.margin}-point margin
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* Angler leaderboard */}
          {anglers.length > 0 && (
            <div className="pub-year-section">
              <div className="pub-year-section-title">Angler Leaderboard</div>
              <div className="pub-leaderboard">
                {anglers.map((p, i) => (
                  <div key={p.participants.id} className="pub-lb-row">
                    <div className="pub-lb-rank">{i + 1}</div>
                    <div className="pub-lb-name">
                      <Link to={`/participant/${p.participants.id}`} className="pub-lb-link">
                        {p.participants.name}
                      </Link>
                    </div>
                    {p.team && (
                      <span className={`pub-lb-team ${p.team === 'A' ? 'pub-team-a' : 'pub-team-b'}`}>
                        Team {p.team}
                      </span>
                    )}
                    <div className="pub-lb-score">{p.score ?? '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Guide leaderboard */}
          {guides.length > 0 && (
            <div className="pub-year-section">
              <div className="pub-year-section-title">Guide Leaderboard</div>
              <div className="pub-leaderboard">
                {guides.map((p, i) => (
                  <div key={p.participants.id} className="pub-lb-row">
                    <div className="pub-lb-rank">{i + 1}</div>
                    <div className="pub-lb-name">
                      <Link to={`/participant/${p.participants.id}`} className="pub-lb-link">
                        {p.participants.name}
                      </Link>
                    </div>
                    {p.team && (
                      <span className={`pub-lb-team ${p.team === 'A' ? 'pub-team-a' : 'pub-team-b'}`}>
                        Team {p.team}
                      </span>
                    )}
                    <div className="pub-lb-score">{p.score ?? '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Catch breakdown */}
          {catchRows.length > 0 && (
            <div className="pub-year-section">
              <div className="pub-year-section-title">Catch Breakdown</div>
              <div className="pub-catch-table">
                <div className="pub-catch-header">
                  <span>Species</span>
                  <span>Catches</span>
                  <span>Pts Each</span>
                  <span>Total Pts</span>
                </div>
                {catchRows.map(c => (
                  <div key={c.species} className="pub-catch-row">
                    <span className="pub-catch-species">{c.species}</span>
                    <span>{c.count}</span>
                    <span>{c.pts_each}</span>
                    <span className="pub-catch-total">{c.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Photo gallery */}
          <div className="pub-year-section">
            <div className="pub-year-section-title">Photo Gallery</div>
            {photos.length === 0 ? (
              <p className="pub-no-results">No approved photos for {tournament.year} yet.</p>
            ) : (
              <div className="pub-gallery-grid">
                {photos.map(m => (
                  <div key={m.id} className="pub-gallery-item" onClick={() => setLbPhoto(m)}>
                    <img
                      src={m.signedUrl}
                      alt={m.caption || `${tournament.year} tournament`}
                      className="pub-gallery-img"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Lightbox */}
      {lbPhoto && (
        <div className="pub-gallery-lb-overlay" onClick={() => setLbPhoto(null)}>
          <button className="pub-gallery-lb-close" onClick={e => { e.stopPropagation(); setLbPhoto(null) }}>✕</button>
          <img
            src={lbPhoto.signedUrl}
            alt={lbPhoto.caption || 'Tournament photo'}
            className="pub-gallery-lb-img"
            onClick={e => e.stopPropagation()}
          />
          {lbPhoto.caption && (
            <div className="pub-gallery-lb-caption" onClick={e => e.stopPropagation()}>
              {lbPhoto.caption}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── ABOUT PAGE ──────────────────────────────────────────────────────────────
export function AboutPage() {
  const [featuredPhotos, setFeaturedPhotos] = useState([])

  useEffect(() => {
    const load = async () => {
      const { data: rows } = await supabase
        .from('media')
        .select('id, file_url, caption')
        .eq('status', 'approved')
        .eq('featured', true)
        .eq('file_type', 'photo')
        .limit(3)

      if (!rows?.length) return

      const { data: signed } = await supabase.storage
        .from('tarpon-media')
        .createSignedUrls(rows.map(r => r.file_url), 3600)

      setFeaturedPhotos(
        rows.map((r, i) => ({ ...r, signedUrl: signed?.[i]?.signedUrl || null }))
            .filter(r => r.signedUrl)
      )
    }
    load()
  }, [])

  const photo = (index) => {
    const p = featuredPhotos[index]
    if (!p) return null
    return (
      <div className="pub-about-photo-break">
        <img src={p.signedUrl} alt={p.caption || 'Tournament photo'} className="pub-about-photo-img" />
        {p.caption && <div className="pub-about-photo-caption">{p.caption}</div>}
      </div>
    )
  }

  return (
    <div className="pub-results-page-wrap">
      <PublicNav />

      {/* Hero */}
      <div className="pub-aj-hero">
        <div className="pub-aj-hero-inner">
          <h1 className="pub-aj-hero-title">About The Tarpon Memorial</h1>
        </div>
      </div>

      <div className="pub-about-body">
        <div className="pub-about-container">

          <div className="pub-about-section">
            <h2 className="pub-about-heading">Seventeen Years on the Water</h2>
            <p className="pub-about-body-text">For seventeen years, a group of friends has made the journey to Islamorada, Florida — loading up at Bud N' Mary's Marina at 4PM on the first night, heading out into the backcountry, and doing battle with one of the most powerful fish in the world. The Silver King. The Tarpon.</p>
            <p className="pub-about-body-text">What started in 2009 as four friends with a simple question — who can catch the most? — has grown into one of the most anticipated weeks of the year for everyone involved. No real sponsors. No media. No public leaderboard. Just a tight group of anglers, world-class guides, and seventeen years of stories that keep getting better.</p>
          </div>

          {photo(0)}

          <div className="pub-about-section">
            <h2 className="pub-about-heading">How It Grew</h2>
            <p className="pub-about-body-text">The first tournament was four friends, a handshake, and a friendly competition. By 2010 we needed opponents, so four more joined and the team format was born. By 2014 the fish stories had spread far enough that four more were added, bringing the tournament to its current format — twelve anglers split into two teams of six, each team with three guides, two anglers per boat.</p>
            <p className="pub-about-body-text">In 2016 an app was introduced and everything changed. Every time anyone in the tournament catches a fish, every phone in the group dings. You never know if it is your team or theirs posting the catch. It added a level of excitement — and stress — that nobody expected. The sound of that notification has become one of the most recognizable sounds of tournament week.</p>
          </div>

          <div className="pub-about-section">
            <h2 className="pub-about-heading">How It Works</h2>
            <p className="pub-about-body-text">The tournament runs on a simple but competitive format. Teams fish separately during the day — each boat working its own water, chasing its own fish. But when the lines come in, everyone comes together. Lunch and dinner are a group affair. The competition is on the water. The camaraderie is everywhere else.</p>
            <p className="pub-about-body-text">Prize money goes to the winning guides — they earn it. The top individual angler takes home a prize of their own. But if we are being honest, this tournament runs on bragging rights. The right to talk about it for the next twelve months until everyone is back on the dock at Bud N' Mary's.</p>
          </div>

          {photo(1)}

          <div className="pub-about-section">
            <h2 className="pub-about-heading">Tournament Week</h2>
            <p className="pub-about-body-text">Anglers fly in from all over the country and converge on South Florida, making the drive down to the Keys in time for the first charter at 4PM. From that moment the tournament is live. The next few days are a blur of early mornings, long runs into the backcountry, tight lines, blown shots, incredible catches, cold drinks, good food, and cigars. Lots of cigars.</p>
            <p className="pub-about-body-text">We fish hard. We eat well. We tell stories that grow with every telling. For a few days, whatever is happening back home simply does not exist.</p>
          </div>

          <div className="pub-about-section">
            <h2 className="pub-about-heading">Getting In</h2>
            <p className="pub-about-body-text">This is an invite-only tournament. There is no application, no waitlist, no public registration. If you know someone already in the group and a spot opens up, they can put your name forward. That is the only way in.</p>
            <p className="pub-about-body-text">The core group has fished together for years. The guides know us. We know the guides. The tournament is well known at Bud N' Mary's — which at the Sportfishing Capital of the World, means something. There are many characters in this group. Hazing is a tradition. Thick skin is recommended.</p>
          </div>

          {photo(2)}

          {/* Closing pull quote */}
          <div className="pub-about-close-quote">
            <div className="pub-about-close-rule" />
            <blockquote className="pub-about-close-text">
              The Tarpon Memorial is not really about the fish. It never was. It is about a group of people who carved out a few days every year to step away from everything and just be present — on the water, at the table, in the moment. The fishing is the reason we show up. The friendships are the reason we keep coming back.
            </blockquote>
            <div className="pub-about-close-rule" />
          </div>

        </div>
      </div>
    </div>
  )
}

// ─── AUSTIN JONES PAGE ───────────────────────────────────────────────────────
export function AustinJonesPage() {
  return (
    <div className="pub-results-page-wrap">
      <PublicNav />

      {/* Hero banner */}
      <div className="pub-aj-hero">
        <div className="pub-aj-hero-inner">
          <h1 className="pub-aj-hero-title">In Memory of Austin Jones</h1>
          <div className="pub-aj-hero-dates">October 14, 1931 — August 2, 2017</div>
        </div>
      </div>

      <div className="pub-aj-body">
        <div className="pub-aj-container">

          {/* Two-column intro */}
          <div className="pub-aj-intro">
            <div className="pub-aj-intro-left">
              <img src={austinJonesPhoto} alt="Austin Jones" className="pub-aj-photo" />
            </div>
            <div className="pub-aj-intro-right">
              <div className="pub-aj-pull-quote">
                "He was already in his seventies, but he carried himself like a man who had never once considered slowing down."
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div className="pub-aj-section">
            <h2 className="pub-aj-section-heading">How It All Started</h2>
            <p className="pub-aj-section-body">Austin Jones walked into Ron's cigar store in the early 2000s as a customer. He left as a friend — the kind you don't find very often. Austin was already in his seventies, but he carried himself like a man who had never once considered slowing down. He had spent decades in the oil and gas industry, risen to Corporate Vice President, testified at The Hague International Court, and shared podiums with Nobel Laureates. But none of that mattered much when the conversation turned to fishing.</p>
            <p className="pub-aj-section-body">In 2008, Ron decided he wanted to chase tarpon in Florida and mentioned it to Austin. Without hesitation, Austin said he was in. The two of them headed to Captiva, FL with a guide and not a single tarpon to show for it — but something else was born on that trip. A tradition. The following year they brought two more friends and moved the outing to Islamorada in the Florida Keys. A tournament grew from there. Austin fished with the group in 2009 and 2010, loading up on the boat and heading out to the backcountry waters at nearly 80 years old, doing one of the things he loved, fishing with friends.</p>
          </div>

          {/* Section 2 */}
          <div className="pub-aj-section">
            <h2 className="pub-aj-section-heading">The Man Behind The Name</h2>
            <p className="pub-aj-section-body">Austin Thomas Jones was born on October 14, 1931 in humble beginnings — literally in a tent behind his grandmother's store in Corsicana, Texas. He graduated high school at 15, was admitted to Hardin Simmons University that same year, and served as an Air Force officer during the Korean War era, training as a gunner on B-29s before being commissioned as a Second Lieutenant.</p>
            <p className="pub-aj-section-body">After the military, Austin built a remarkable career in the oil and gas industry — driving mud and cement trucks, working the rigs, climbing through sales and management at companies like Halliburton and BJ Services, eventually becoming Vice President of International Sales at Dyna-Drill and later Corporate Vice President at Smith International. He traveled the world roughly six months of every year. When Smith International entered bankruptcy in 1987, Austin didn't skip a beat. He started his own consulting company, bought into an industrial diamond business called Diamond Scientific, and helped build it into a success as President and CEO. That was Austin — he always landed on his feet.</p>
            <p className="pub-aj-section-body">But the people who knew him best didn't talk about his résumé. They talked about his smile. His stories. The way he made every person in the room feel like the most important one there. No man was a stranger to Austin Jones. He was married to his beloved Margaret for 47 years until her passing in 2001. Austin had a way of summing it up perfectly — he would say they were married for 92 years. Forty-six years of days and forty-six years of nights.</p>
          </div>

          {/* Section 3 */}
          <div className="pub-aj-section">
            <h2 className="pub-aj-section-heading">Why We Dedicate This Tournament To Him</h2>
            <p className="pub-aj-section-body">Austin Jones knew everyone. And we mean everyone. Dinners out, charity functions, trips around the world — it didn't matter where you were or who was in the room. Within minutes someone would holler across the restaurant, lobby or airport — <span className="pub-aj-shout">AUSTIN JONES!</span> — and Austin would turn around, break into that smile, and greet another long-term friend like no time had passed at all. Major sports athletes. Team owners. Oil executives. Everyday people. It made no difference to Austin. He treated them all the same.</p>
            <p className="pub-aj-section-body">The stories were endless and remarkable. The kind you couldn't make up. He had lived so much life and touched so many people that an evening with Austin was never just dinner — it was an education, a comedy show, and a reminder of what it means to really live.</p>
            <p className="pub-aj-section-body">As Austin got older, the group started calling the tournament The Austin Jones Memorial — while he was still very much alive and laughing about it. He was two decades older than anyone else on the water. He fished hard, told stories harder, and never once complained. It felt right to honor him while he was still around to enjoy it. When Austin stepped back from the tournament, we kept his name right where it belonged. Not as a formality — but as a genuine tribute to the man who started all of this with a simple answer when Ron mentioned wanting to chase tarpon in Florida. He said yes. And here we are.</p>
          </div>

          {/* Closing memorial */}
          <div className="pub-aj-memorial">
            <p>Austin Jones passed away on August 2, 2017 at the age of 85 in his home in Houston, Texas. The tournament he helped start lives on in his honor.</p>
          </div>

        </div>
      </div>
    </div>
  )
}

// ─── PARTICIPANT PROFILE PAGE (/participant/:id) ──────────────────────────────
export function ParticipantPage() {
  const { id } = useParams()

  const [loading, setLoading]             = useState(true)
  const [notFound, setNotFound]           = useState(false)
  const [participant, setParticipant]     = useState(null)
  const [yearlyResults, setYearlyResults] = useState([])
  const [catches, setCatches]             = useState([])
  const [media, setMedia]                 = useState([])
  const [lbPhoto, setLbPhoto]             = useState(null)

  useEffect(() => {
    const numId = parseInt(id, 10)
    if (isNaN(numId)) { setNotFound(true); setLoading(false); return }

    const load = async () => {
      setLoading(true)
      setNotFound(false)

      // Round 1: participant identity (need role for rank query)
      const { data: p } = await supabase
        .from('participants')
        .select('id, name, role')
        .eq('id', numId)
        .maybeSingle()

      if (!p) { setNotFound(true); setLoading(false); return }
      setParticipant(p)

      // Round 2: yearly results + catches + media ids in parallel
      const [{ data: yr }, { data: cs }, { data: tags }] = await Promise.all([
        supabase.from('tournament_participants')
          .select('tournament_year, score, team')
          .eq('participant_id', numId)
          .order('tournament_year', { ascending: false }),
        supabase.from('catches')
          .select('tournament_year, species, points, catch_type')
          .eq('participant_id', numId),
        supabase.from('media_participant_tags')
          .select('media_id')
          .eq('participant_id', numId),
      ])

      const years    = (yr   || []).map(r => r.tournament_year)
      const mediaIds = (tags || []).map(t => t.media_id)

      // Round 3: peer scores for ranks + approved photos in parallel
      const [{ data: peers }, { data: mediaRows }] = await Promise.all([
        years.length > 0
          ? supabase.from('tournament_participants')
              .select('tournament_year, score')
              .in('tournament_year', years)
              .eq('role', p.role)
              .not('score', 'is', null)
          : Promise.resolve({ data: [] }),
        mediaIds.length > 0
          ? supabase.from('media')
              .select('id, file_url, file_type, caption, tournament_year')
              .in('id', mediaIds)
              .eq('status', 'approved')
              .eq('file_type', 'photo')
          : Promise.resolve({ data: [] }),
      ])

      // Compute rank per year
      const peersByYear = {}
      for (const peer of (peers || [])) {
        if (!peersByYear[peer.tournament_year]) peersByYear[peer.tournament_year] = []
        peersByYear[peer.tournament_year].push(peer.score)
      }
      const rankedYears = (yr || []).map(r => {
        const yp   = peersByYear[r.tournament_year] || []
        const rank = yp.filter(s => s > (r.score || 0)).length + 1
        return { ...r, rank, total_in_role: yp.length }
      })

      setYearlyResults(rankedYears)
      setCatches(cs || [])

      if (mediaRows?.length) {
        const { data: signed } = await supabase.storage
          .from('tarpon-media')
          .createSignedUrls(mediaRows.map(m => m.file_url), 3600)
        setMedia(mediaRows.map((m, i) => ({ ...m, signedUrl: signed?.[i]?.signedUrl || null })))
      }

      setLoading(false)
    }
    load()
  }, [id])

  useEffect(() => {
    if (!lbPhoto) return
    const onKey = e => { if (e.key === 'Escape') setLbPhoto(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lbPhoto])

  if (loading) return (
    <div className="pub-results-page-wrap">
      <PublicNav />
      <div className="pub-part-body">
        <div className="pub-loading" style={{ padding: '4rem 0', textAlign: 'center' }}>Loading…</div>
      </div>
    </div>
  )

  if (notFound) return <Navigate to="/results" replace />

  // ── Derived stats ────────────────────────────────────────────────────────────
  const scores     = yearlyResults.filter(r => r.score != null).map(r => r.score)
  const totalPts   = scores.reduce((a, b) => a + b, 0)
  const bestRow    = yearlyResults.reduce((best, r) => (r.score || 0) > (best?.score || 0) ? r : best, null)
  const sortedYrs  = [...yearlyResults].sort((a, b) => a.tournament_year - b.tournament_year)
  const firstYear  = sortedYrs[0]?.tournament_year
  const latestYear = sortedYrs[sortedYrs.length - 1]?.tournament_year

  // Tarpon stats — species names in DB are "Tarpon Catch" and "Tarpon Release"
  const tarponCaught   = catches.filter(c => c.species === 'Tarpon Catch').length
  const tarponReleased = catches.filter(c => c.species === 'Tarpon Release').length
  const tarponTotal    = tarponCaught + tarponReleased

  // Career catch breakdown grouped by species
  const careerMap = {}
  for (const c of catches) {
    if (!careerMap[c.species]) careerMap[c.species] = { species: c.species, count: 0, pts_each: c.points, total: 0 }
    careerMap[c.species].count++
    careerMap[c.species].total += c.points
  }
  const careerRows = Object.values(careerMap).sort((a, b) => b.total - a.total)

  // Catch breakdown per year
  const catchYearMap = {}
  for (const c of catches) {
    if (!catchYearMap[c.tournament_year]) catchYearMap[c.tournament_year] = {}
    if (!catchYearMap[c.tournament_year][c.species])
      catchYearMap[c.tournament_year][c.species] = { species: c.species, count: 0, pts_each: c.points, total: 0 }
    catchYearMap[c.tournament_year][c.species].count++
    catchYearMap[c.tournament_year][c.species].total += c.points
  }
  const catchByYear = Object.entries(catchYearMap)
    .sort(([a], [b]) => parseInt(b) - parseInt(a))
    .map(([yr, sm]) => ({ year: parseInt(yr), rows: Object.values(sm).sort((a, b) => b.total - a.total) }))

  const photos    = media.filter(m => m.signedUrl)
  const roleLabel = participant.role === 'angler' ? 'Angler' : 'Guide'

  return (
    <div className="pub-results-page-wrap">
      <PublicNav />
      <div className="pub-part-body">
        <div className="pub-part-container">

          {/* Header */}
          <div className="pub-part-header">
            <span className={`pub-part-role-badge ${participant.role === 'angler' ? 'pub-part-role-angler' : 'pub-part-role-guide'}`}>
              {roleLabel}
            </span>
            <h1 className="pub-part-name">{participant.name}</h1>
            <div className="pub-part-years-count">
              {yearlyResults.length} tournament{yearlyResults.length !== 1 ? 's' : ''}
            </div>
            {sortedYrs.length > 0 && (
              <div className="pub-part-year-badges">
                {sortedYrs.map(r => (
                  <Link key={r.tournament_year} to={`/results/${r.tournament_year}`} className="pub-part-year-badge">
                    {r.tournament_year}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Career stats */}
          <div className="pub-year-section">
            <div className="pub-year-section-title">Career Stats</div>
            <div className="pub-stats-grid">
              <div className="pub-stat-card">
                <div className="pub-stat-value">{totalPts}</div>
                <div className="pub-stat-label">Career Points</div>
              </div>
              {bestRow && (
                <div className="pub-stat-card">
                  <div className="pub-stat-value">{bestRow.score}</div>
                  <div className="pub-stat-label">Best Year ({bestRow.tournament_year})</div>
                </div>
              )}
              <div className="pub-stat-card">
                <div className="pub-stat-value">{yearlyResults.length}</div>
                <div className="pub-stat-label">Years Competed</div>
              </div>
              {firstYear && (
                <div className="pub-stat-card">
                  <div className="pub-stat-value">{firstYear}</div>
                  <div className="pub-stat-label">First Year</div>
                </div>
              )}
              {latestYear && latestYear !== firstYear && (
                <div className="pub-stat-card">
                  <div className="pub-stat-value">{latestYear}</div>
                  <div className="pub-stat-label">Most Recent</div>
                </div>
              )}
              {tarponTotal > 0 && <>
                <div className="pub-stat-card">
                  <div className="pub-stat-value">{tarponCaught}</div>
                  <div className="pub-stat-label">Tarpon Caught</div>
                </div>
                <div className="pub-stat-card">
                  <div className="pub-stat-value">{tarponReleased}</div>
                  <div className="pub-stat-label">Tarpon Released</div>
                </div>
                <div className="pub-stat-card">
                  <div className="pub-stat-value">{tarponTotal}</div>
                  <div className="pub-stat-label">Total Tarpon</div>
                </div>
              </>}
            </div>
          </div>

          {/* Year by year results */}
          {yearlyResults.length > 0 && (
            <div className="pub-year-section">
              <div className="pub-year-section-title">Year by Year</div>
              <div className="pub-yr-table">
                <div className="pub-yr-header">
                  <span>Year</span>
                  <span>Score</span>
                  <span>Team</span>
                  <span>Rank</span>
                  <span></span>
                </div>
                {yearlyResults.map(r => (
                  <div key={r.tournament_year} className="pub-yr-row">
                    <span className="pub-yr-year">{r.tournament_year}</span>
                    <span className="pub-yr-score">{r.score ?? '—'}</span>
                    <span>
                      {r.team
                        ? <span className={`pub-lb-team ${r.team === 'Team A' ? 'pub-team-a' : 'pub-team-b'}`}>{r.team}</span>
                        : <span className="pub-yr-dim">—</span>
                      }
                    </span>
                    <span className="pub-yr-rank">
                      {r.rank ? `#${r.rank} of ${r.total_in_role}` : '—'}
                    </span>
                    <span>
                      <Link to={`/results/${r.tournament_year}`} className="pub-result-link" style={{ fontSize: '0.8rem' }}>
                        View →
                      </Link>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Career catch breakdown */}
          {careerRows.length > 0 && (
            <div className="pub-year-section">
              <div className="pub-year-section-title">Career Catch Breakdown</div>
              <div className="pub-catch-table">
                <div className="pub-catch-header">
                  <span>Species</span><span>Catches</span><span>Pts Each</span><span>Total Pts</span>
                </div>
                {careerRows.map(c => (
                  <div key={c.species} className="pub-catch-row">
                    <span className="pub-catch-species">{c.species}</span>
                    <span>{c.count}</span>
                    <span>{c.pts_each}</span>
                    <span className="pub-catch-total">{c.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Catch breakdown by year */}
          {catchByYear.length > 0 && (
            <div className="pub-year-section">
              <div className="pub-year-section-title">Catch Breakdown by Year</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {catchByYear.map(({ year, rows }) => (
                  <div key={year}>
                    <div className="pub-catch-year-label">{year}</div>
                    <div className="pub-catch-table">
                      <div className="pub-catch-header">
                        <span>Species</span><span>Catches</span><span>Pts Each</span><span>Total Pts</span>
                      </div>
                      {rows.map(c => (
                        <div key={c.species} className="pub-catch-row">
                          <span className="pub-catch-species">{c.species}</span>
                          <span>{c.count}</span>
                          <span>{c.pts_each}</span>
                          <span className="pub-catch-total">{c.total}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Photos */}
          {photos.length > 0 && (
            <div className="pub-year-section">
              <div className="pub-year-section-title">Photos</div>
              <div className="pub-gallery-grid">
                {photos.map(m => (
                  <div key={m.id} className="pub-gallery-item" onClick={() => setLbPhoto(m)}>
                    <img src={m.signedUrl} alt={m.caption || participant.name} className="pub-gallery-img" />
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {lbPhoto && (
        <div className="pub-gallery-lb-overlay" onClick={() => setLbPhoto(null)}>
          <button className="pub-gallery-lb-close" onClick={e => { e.stopPropagation(); setLbPhoto(null) }}>✕</button>
          <img
            src={lbPhoto.signedUrl}
            alt={lbPhoto.caption || participant.name}
            className="pub-gallery-lb-img"
            onClick={e => e.stopPropagation()}
          />
          {lbPhoto.caption && (
            <div className="pub-gallery-lb-caption" onClick={e => e.stopPropagation()}>
              {lbPhoto.caption}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── RESULTS INDEX PAGE ───────────────────────────────────────────────────────
export function ResultsIndexPage() {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      supabase.from('tournaments').select('year, cancelled').order('year', { ascending: false }),
      supabase.from('year_winners').select('year, team_a_score, team_b_score, winning_team'),
      supabase.from('top_anglers_by_year').select('tournament_year, name, score'),
      supabase.from('top_guides_by_year').select('tournament_year, name, score'),
    ]).then(([{ data: allYears }, { data: winners }, { data: anglers }, { data: guides }]) => {
      const winnerMap  = Object.fromEntries((winners || []).map(w => [w.year, w]))
      const anglerMap  = Object.fromEntries((anglers || []).map(a => [a.tournament_year, a]))
      const guideMap   = Object.fromEntries((guides  || []).map(g => [g.tournament_year, g]))

      setResults((allYears || []).map(t => ({
        year:       t.year,
        cancelled:  t.cancelled,
        winner:     winnerMap[t.year]  || null,
        top_angler: anglerMap[t.year]  || null,
        top_guide:  guideMap[t.year]   || null,
      })))
      setLoading(false)
    })
  }, [])

  return (
    <div className="pub-results-page-wrap">
      <PublicNav />
      <div className="pub-results-body">
        <div className="pub-container">
          <Link to="/" className="pub-back-link">← Back to Home</Link>
          <h1 className="pub-page-title">Tournament Results</h1>
          {loading ? (
            <div className="pub-loading">Loading…</div>
          ) : results.length === 0 ? (
            <p className="pub-no-results">No results posted yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {results.map(r => (
                  <Link
                    key={r.year}
                    to={`/results/${r.year}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <div
                      style={{
                        background: '#fff',
                        border: '1px solid var(--fk-border)',
                        borderRadius: '12px',
                        padding: '1.25rem 1.75rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'box-shadow 0.14s',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--fk-shadow)'}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                    >
                      <div>
                        <div style={{ fontFamily: 'var(--fk-font-display)', fontSize: '1.2rem', fontWeight: 700, color: 'var(--fk-text)' }}>
                          {r.year}
                          {r.cancelled && (
                            <span style={{ fontSize: '0.7rem', marginLeft: '0.6rem', color: 'var(--fk-text-dim)', fontFamily: 'var(--fk-font-body)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                              Cancelled
                            </span>
                          )}
                        </div>
                        {r.winner?.winning_team && (
                          <div style={{ fontSize: '0.85rem', color: 'var(--fk-text-mid)', marginTop: '0.2rem' }}>
                            {r.winner.winning_team} Wins
                          </div>
                        )}
                        {(r.top_angler || r.top_guide) && (
                          <div style={{ fontSize: '0.78rem', color: 'var(--fk-text-dim)', marginTop: '0.15rem' }}>
                            {r.top_angler && <>Top Angler: {r.top_angler.name} ({r.top_angler.score} pts)</>}
                            {r.top_angler && r.top_guide && <span style={{ margin: '0 0.4rem' }}>·</span>}
                            {r.top_guide && <>Top Guide: {r.top_guide.name} ({r.top_guide.score} pts)</>}
                          </div>
                        )}
                      </div>
                      {r.winner && (
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1.5rem' }}>
                          <div style={{ fontSize: '0.75rem', color: 'var(--fk-text-dim)', marginBottom: '0.2rem' }}>
                            Team A vs Team B
                          </div>
                          <div style={{ fontFamily: 'var(--fk-font-display)', fontWeight: 700, color: 'var(--fk-blue)', fontSize: '1.1rem' }}>
                            {r.winner.team_a_score} – {r.winner.team_b_score}
                          </div>
                        </div>
                      )}
                    </div>
                  </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── RECORDS & LEADERBOARDS PAGE (/records) ───────────────────────────────────
export function RecordsPage() {
  const [loading, setLoading] = useState(true)
  const [rec, setRec]         = useState(null)

  useEffect(() => {
    const load = async () => {
      const [
        { data: winners },
        { data: allTournaments },
        { data: anglerRows },
        { data: guideRows },
        { data: anglerTarpon },
        { data: guideTarpon },
        { data: topAnglersByYear },
        { data: topGuidesByYear },
      ] = await Promise.all([
        supabase.from('year_winners').select('year, team_a_score, team_b_score, winning_team').order('year'),
        supabase.from('tournaments').select('year, cancelled').order('year', { ascending: false }),
        supabase.from('tournament_participants')
          .select('participant_id, tournament_year, score, participants(id, name)')
          .eq('role', 'angler'),
        supabase.from('tournament_participants')
          .select('participant_id, tournament_year, score, participants(id, name)')
          .eq('role', 'guide'),
        supabase.from('catches')
          .select('participant_id, tournament_year, species, participants!inner(role)')
          .eq('participants.role', 'angler')
          .in('species', ['Tarpon Catch', 'Tarpon Release']),
        supabase.from('catches')
          .select('participant_id, tournament_year, species, participants!inner(role)')
          .eq('participants.role', 'guide')
          .in('species', ['Tarpon Catch', 'Tarpon Release']),
        supabase.from('top_anglers_by_year').select('tournament_year, name, score'),
        supabase.from('top_guides_by_year').select('tournament_year, name, score'),
      ])

      // ── Team all-time record
      const ws = winners || []
      const teamAWins    = ws.filter(w => w.winning_team === 'Team A').length
      const teamBWins    = ws.filter(w => w.winning_team === 'Team B').length
      const teamATotalPts = ws.reduce((s, w) => s + (w.team_a_score || 0), 0)
      const teamBTotalPts = ws.reduce((s, w) => s + (w.team_b_score || 0), 0)
      const margins = ws
        .filter(w => w.team_a_score != null && w.team_b_score != null)
        .map(w => ({ year: w.year, margin: Math.abs(w.team_a_score - w.team_b_score), winner: w.winning_team, scoreA: w.team_a_score, scoreB: w.team_b_score }))
        .filter(m => m.margin > 0)
      const biggestMargin = margins.length ? margins.reduce((b, m) => m.margin > b.margin ? m : b, margins[0]) : null
      const closestMargin = margins.length ? margins.reduce((b, m) => m.margin < b.margin ? m : b, margins[0]) : null

      // ── Angler leaderboard
      const anglerMap = {}
      for (const row of (anglerRows || [])) {
        const pid = row.participant_id
        const p   = row.participants
        if (!p) continue
        if (!anglerMap[pid]) anglerMap[pid] = { id: p.id, name: p.name, years: 0, totalPts: 0, bestScore: 0, bestYear: null, tarponCaught: 0, tarponReleased: 0 }
        anglerMap[pid].years++
        anglerMap[pid].totalPts += (row.score || 0)
        if ((row.score || 0) > anglerMap[pid].bestScore) {
          anglerMap[pid].bestScore = row.score || 0
          anglerMap[pid].bestYear  = row.tournament_year
        }
      }
      for (const c of (anglerTarpon || [])) {
        if (anglerMap[c.participant_id]) {
          if (c.species === 'Tarpon Catch')   anglerMap[c.participant_id].tarponCaught++
          if (c.species === 'Tarpon Release') anglerMap[c.participant_id].tarponReleased++
        }
      }
      const anglerLeaderboard = Object.values(anglerMap).sort((a, b) => b.totalPts - a.totalPts)

      // ── Guide leaderboard
      const guideMap = {}
      for (const row of (guideRows || [])) {
        const pid = row.participant_id
        const p   = row.participants
        if (!p) continue
        if (!guideMap[pid]) guideMap[pid] = { id: p.id, name: p.name, years: 0, totalPts: 0, bestScore: 0, bestYear: null, tarponCaught: 0, tarponReleased: 0 }
        guideMap[pid].years++
        guideMap[pid].totalPts += (row.score || 0)
        if ((row.score || 0) > guideMap[pid].bestScore) {
          guideMap[pid].bestScore = row.score || 0
          guideMap[pid].bestYear  = row.tournament_year
        }
      }
      for (const c of (guideTarpon || [])) {
        if (guideMap[c.participant_id]) {
          if (c.species === 'Tarpon Catch')   guideMap[c.participant_id].tarponCaught++
          if (c.species === 'Tarpon Release') guideMap[c.participant_id].tarponReleased++
        }
      }
      const guideLeaderboard = Object.values(guideMap).sort((a, b) => b.totalPts - a.totalPts)

      // ── Tournament records
      let highTeamScore = 0, highTeamYear = null, highTeamName = null
      let lowWinScore = Infinity, lowWinYear = null
      for (const w of ws) {
        if ((w.team_a_score || 0) > highTeamScore) { highTeamScore = w.team_a_score; highTeamYear = w.year; highTeamName = 'Team A' }
        if ((w.team_b_score || 0) > highTeamScore) { highTeamScore = w.team_b_score; highTeamYear = w.year; highTeamName = 'Team B' }
        const winScore = w.winning_team === 'Team A' ? w.team_a_score : (w.winning_team === 'Team B' ? w.team_b_score : null)
        if (winScore != null && winScore > 0 && winScore < lowWinScore) { lowWinScore = winScore; lowWinYear = w.year }
      }

      const tarponByYear = {}
      for (const c of (anglerTarpon || [])) {
        if (c.species === 'Tarpon Catch')
          tarponByYear[c.tournament_year] = (tarponByYear[c.tournament_year] || 0) + 1
      }
      const tarponYearBest  = Object.entries(tarponByYear).sort(([,a],[,b]) => b - a)[0] || null
      const anglerByTarpon  = Object.values(anglerMap).sort((a, b) => b.tarponCaught - a.tarponCaught)[0] || null
      const guideByTarpon   = Object.values(guideMap).sort((a, b) => b.tarponCaught - a.tarponCaught)[0] || null

      // ── Timeline
      const winnersByYear   = Object.fromEntries(ws.map(w => [w.year, w]))
      const topAnglerMap    = Object.fromEntries((topAnglersByYear || []).map(a => [a.tournament_year, a]))
      const topGuideMap     = Object.fromEntries((topGuidesByYear  || []).map(g => [g.tournament_year, g]))
      const timeline = (allTournaments || []).map(t => ({
        year:      t.year,
        cancelled: t.cancelled,
        winner:    winnersByYear[t.year] || null,
        topAngler: topAnglerMap[t.year]  || null,
        topGuide:  topGuideMap[t.year]   || null,
      }))

      setRec({
        teamAWins, teamBWins, teamATotalPts, teamBTotalPts,
        totalYears: ws.length,
        biggestMargin, closestMargin,
        anglerLeaderboard, guideLeaderboard,
        highTeamScore, highTeamYear, highTeamName,
        lowWinScore: lowWinScore === Infinity ? null : lowWinScore, lowWinYear,
        tarponYearBest, anglerByTarpon, guideByTarpon,
        timeline,
      })
      setLoading(false)
    }
    load()
  }, [])

  if (loading) return (
    <div className="pub-results-page-wrap">
      <PublicNav />
      <div className="pub-loading" style={{ padding: '5rem 0', textAlign: 'center' }}>Loading…</div>
    </div>
  )

  const {
    teamAWins, teamBWins, teamATotalPts, teamBTotalPts, totalYears,
    biggestMargin, closestMargin,
    anglerLeaderboard, guideLeaderboard,
    highTeamScore, highTeamYear, highTeamName,
    lowWinScore, lowWinYear,
    tarponYearBest, anglerByTarpon, guideByTarpon,
    timeline,
  } = rec

  const HofTable = ({ rows }) => (
    <div className="pub-rec-hof-table">
      <div className="pub-rec-hof-header">
        <span>#</span>
        <span>Name</span>
        <span>Yrs</span>
        <span>Career Pts</span>
        <span className="pub-rec-hof-hide-sm">Best Year</span>
        <span className="pub-rec-hof-hide-sm">Leader</span>
        <span className="pub-rec-hof-hide-sm">Release</span>
      </div>
      {rows.map((r, i) => (
        <div key={r.id} className="pub-rec-hof-row">
          <span className="pub-rec-hof-rank">{i + 1}</span>
          <span className="pub-rec-hof-name-cell">
            <Link to={`/participant/${r.id}`} className="pub-result-link">{r.name}</Link>
            <span className="pub-rec-hof-tarpon-sub">Leader: {r.tarponCaught || '—'} · Release: {r.tarponReleased || '—'}</span>
          </span>
          <span className="pub-rec-hof-dim">{r.years}</span>
          <span className="pub-rec-hof-pts">{r.totalPts}</span>
          <span className="pub-rec-hof-dim pub-rec-hof-hide-sm">
            {r.bestYear ? <>{r.bestScore} <span className="pub-rec-hof-sub">({r.bestYear})</span></> : '—'}
          </span>
          <span className="pub-rec-hof-dim pub-rec-hof-hide-sm">{r.tarponCaught || '—'}</span>
          <span className="pub-rec-hof-dim pub-rec-hof-hide-sm">{r.tarponReleased || '—'}</span>
        </div>
      ))}
    </div>
  )

  return (
    <div className="pub-results-page-wrap">
      <PublicNav />

      <div className="pub-aj-hero">
        <div className="pub-aj-hero-inner">
          <h1 className="pub-aj-hero-title">Records &amp; Leaderboards</h1>
          <div className="pub-aj-hero-dates">
            {timeline.length > 0 && `${timeline[timeline.length - 1].year}–${timeline[0].year}`}
            {totalYears > 0 && ` · ${totalYears} Tournaments`}
          </div>
        </div>
      </div>

      <div className="pub-rec-body">
        <div className="pub-rec-container">

          {/* ── 1. Team All-Time Record */}
          <div className="pub-rec-section">
            <div className="pub-rec-eyebrow">The All-Time Series</div>
            <h2 className="pub-rec-title">Team Head-to-Head Record</h2>
            <div className="pub-rec-rivalry">
              <div className={`pub-rec-team-col${teamAWins > teamBWins ? ' pub-rec-team-leading' : ''}`}>
                <div className="pub-rec-team-label">Team A</div>
                <div className="pub-rec-team-wins">{teamAWins}</div>
                <div className="pub-rec-team-wins-sub">wins</div>
                <div className="pub-rec-team-pts">{teamATotalPts.toLocaleString()}</div>
                <div className="pub-rec-team-pts-sub">career points</div>
              </div>
              <div className="pub-rec-vs">VS</div>
              <div className={`pub-rec-team-col${teamBWins > teamAWins ? ' pub-rec-team-leading' : ''}`}>
                <div className="pub-rec-team-label">Team B</div>
                <div className="pub-rec-team-wins">{teamBWins}</div>
                <div className="pub-rec-team-wins-sub">wins</div>
                <div className="pub-rec-team-pts">{teamBTotalPts.toLocaleString()}</div>
                <div className="pub-rec-team-pts-sub">career points</div>
              </div>
            </div>
            <div className="pub-rec-rivalry-facts">
              {biggestMargin && (
                <div className="pub-rec-fact">
                  <div className="pub-rec-fact-label">Biggest Margin</div>
                  <div className="pub-rec-fact-value">{biggestMargin.winner} wins · {biggestMargin.scoreA}–{biggestMargin.scoreB} · {biggestMargin.year}</div>
                </div>
              )}
              {closestMargin && (
                <div className="pub-rec-fact">
                  <div className="pub-rec-fact-label">Closest Finish</div>
                  <div className="pub-rec-fact-value">{closestMargin.winner} wins · {closestMargin.scoreA}–{closestMargin.scoreB} · {closestMargin.year}</div>
                </div>
              )}
              <div className="pub-rec-fact">
                <div className="pub-rec-fact-label">Tournaments Played</div>
                <div className="pub-rec-fact-value">{totalYears}</div>
              </div>
            </div>
          </div>

          {/* ── 2. Angler Hall of Fame */}
          <div className="pub-rec-section">
            <div className="pub-rec-eyebrow">All Time</div>
            <h2 className="pub-rec-title">Angler Hall of Fame</h2>
            <HofTable rows={anglerLeaderboard} />
          </div>

          {/* ── 3. Guide Hall of Fame */}
          <div className="pub-rec-section">
            <div className="pub-rec-eyebrow">All Time</div>
            <h2 className="pub-rec-title">Guide Hall of Fame</h2>
            <HofTable rows={guideLeaderboard} />
          </div>

          {/* ── 4. Tournament Records */}
          <div className="pub-rec-section">
            <div className="pub-rec-eyebrow">All Time</div>
            <h2 className="pub-rec-title">Tournament Records</h2>
            <div className="pub-rec-records-grid">
              {highTeamYear && (
                <div className="pub-rec-record-card">
                  <div className="pub-rec-record-value">{highTeamScore}</div>
                  <div className="pub-rec-record-label">Highest Single Team Score</div>
                  <div className="pub-rec-record-sub">{highTeamName} · {highTeamYear}</div>
                </div>
              )}
              {lowWinYear && (
                <div className="pub-rec-record-card">
                  <div className="pub-rec-record-value">{lowWinScore}</div>
                  <div className="pub-rec-record-label">Lowest Winning Score</div>
                  <div className="pub-rec-record-sub">{lowWinYear}</div>
                </div>
              )}
              {biggestMargin && (
                <div className="pub-rec-record-card">
                  <div className="pub-rec-record-value">{biggestMargin.margin}</div>
                  <div className="pub-rec-record-label">Biggest Blowout</div>
                  <div className="pub-rec-record-sub">{biggestMargin.winner} wins · {biggestMargin.scoreA}–{biggestMargin.scoreB} · {biggestMargin.year}</div>
                </div>
              )}
              {closestMargin && (
                <div className="pub-rec-record-card">
                  <div className="pub-rec-record-value">{closestMargin.margin}</div>
                  <div className="pub-rec-record-label">Closest Finish</div>
                  <div className="pub-rec-record-sub">{closestMargin.winner} wins · {closestMargin.scoreA}–{closestMargin.scoreB} · {closestMargin.year}</div>
                </div>
              )}
              {tarponYearBest && (
                <div className="pub-rec-record-card">
                  <div className="pub-rec-record-value">{tarponYearBest[1]}</div>
                  <div className="pub-rec-record-label">Most Tarpon in a Year</div>
                  <div className="pub-rec-record-sub">{tarponYearBest[0]}</div>
                </div>
              )}
              {anglerByTarpon?.tarponCaught > 0 && (
                <div className="pub-rec-record-card">
                  <div className="pub-rec-record-value">{anglerByTarpon.tarponCaught}</div>
                  <div className="pub-rec-record-label">Most Career Tarpon — Angler</div>
                  <div className="pub-rec-record-sub">{anglerByTarpon.name}</div>
                </div>
              )}
              {guideByTarpon?.tarponCaught > 0 && (
                <div className="pub-rec-record-card">
                  <div className="pub-rec-record-value">{guideByTarpon.tarponCaught}</div>
                  <div className="pub-rec-record-label">Most Career Tarpon — Guide</div>
                  <div className="pub-rec-record-sub">{guideByTarpon.name}</div>
                </div>
              )}
            </div>
          </div>

          {/* ── 5. Championship History */}
          <div className="pub-rec-section">
            <div className="pub-rec-eyebrow">Year by Year</div>
            <h2 className="pub-rec-title">Championship History</h2>
            <div className="pub-rec-timeline">
              {timeline.map(t =>
                t.cancelled ? (
                  <div key={t.year} className="pub-rec-yr-card pub-rec-yr-cancelled">
                    <div className="pub-rec-yr-year">{t.year}</div>
                    <div className="pub-rec-yr-cancelled-text">Cancelled</div>
                  </div>
                ) : (
                  <Link key={t.year} to={`/results/${t.year}`} className="pub-rec-yr-card">
                    <div className="pub-rec-yr-year">{t.year}</div>
                    {t.winner && (
                      <>
                        <div className={`pub-rec-yr-winner-badge ${t.winner.winning_team === 'Team A' ? 'pub-rec-yr-a' : 'pub-rec-yr-b'}`}>
                          {t.winner.winning_team} Wins
                        </div>
                        <div className="pub-rec-yr-score">{t.winner.team_a_score} – {t.winner.team_b_score}</div>
                      </>
                    )}
                    {t.topAngler && (
                      <div className="pub-rec-yr-stat">
                        <span className="pub-rec-yr-stat-eyebrow">Top Angler</span>
                        <span className="pub-rec-yr-stat-name">{t.topAngler.name}</span>
                        <span className="pub-rec-yr-stat-score">{t.topAngler.score} pts</span>
                      </div>
                    )}
                    {t.topGuide && (
                      <div className="pub-rec-yr-stat">
                        <span className="pub-rec-yr-stat-eyebrow">Top Guide</span>
                        <span className="pub-rec-yr-stat-name">{t.topGuide.name}</span>
                        <span className="pub-rec-yr-stat-score">{t.topGuide.score} pts</span>
                      </div>
                    )}
                  </Link>
                )
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}

// ─── GALLERY PAGE (/gallery) ──────────────────────────────────────────────────
function groupByYear(list) {
  const map = {}
  for (const m of list) {
    if (!map[m.tournament_year]) map[m.tournament_year] = []
    map[m.tournament_year].push(m)
  }
  return Object.entries(map).sort(([a], [b]) => b - a)
}

export function GalleryPage() {
  const [loading, setLoading]           = useState(true)
  const [allMedia, setAllMedia]         = useState([])
  const [tagMap, setTagMap]             = useState({})
  const [partMap, setPartMap]           = useState({})
  const [tagTypes, setTagTypes]         = useState([])
  const [participants, setParticipants] = useState([])

  const [yearFilter, setYearFilter] = useState('')
  const [tagFilter, setTagFilter]   = useState('')
  const [partFilter, setPartFilter] = useState('')

  const [lbList, setLbList]   = useState([])
  const [lbIndex, setLbIndex] = useState(-1)

  useEffect(() => {
    const load = async () => {
      const { data: mediaRows } = await supabase
        .from('media')
        .select('id, file_url, file_type, caption, tournament_year')
        .eq('status', 'approved')
        .order('tournament_year', { ascending: false })
        .order('created_at', { ascending: false })

      if (!mediaRows?.length) { setLoading(false); return }

      const mediaIds = mediaRows.map(m => m.id)

      const [
        { data: tagTypesData },
        { data: mpt },
        { data: mtt },
        { data: signedUrls },
      ] = await Promise.all([
        supabase.from('tag_types').select('id, name, category').order('name'),
        supabase.from('media_participant_tags').select('media_id, participant_id').in('media_id', mediaIds),
        supabase.from('media_tag_types').select('media_id, tag_type_id').in('media_id', mediaIds),
        supabase.storage.from('tarpon-media').createSignedUrls(mediaRows.map(m => m.file_url), 3600),
      ])

      const uniquePartIds = [...new Set((mpt || []).map(t => t.participant_id))]
      const { data: partRows } = uniquePartIds.length > 0
        ? await supabase.from('participants').select('id, name').in('id', uniquePartIds).order('name')
        : { data: [] }

      const mediaWithUrls = mediaRows
        .map((m, i) => ({ ...m, signedUrl: signedUrls?.[i]?.signedUrl || null }))
        .filter(m => m.signedUrl)

      const tMap = {}
      for (const t of (mtt || [])) {
        if (!tMap[t.media_id]) tMap[t.media_id] = []
        tMap[t.media_id].push(t.tag_type_id)
      }
      const pMap = {}
      for (const t of (mpt || [])) {
        if (!pMap[t.media_id]) pMap[t.media_id] = []
        pMap[t.media_id].push(t.participant_id)
      }

      setAllMedia(mediaWithUrls)
      setTagMap(tMap)
      setPartMap(pMap)
      setTagTypes(tagTypesData || [])
      setParticipants(partRows || [])
      setLoading(false)
    }
    load()
  }, [])

  useEffect(() => {
    if (lbIndex < 0) return
    const onKey = e => {
      if (e.key === 'Escape')     { setLbIndex(-1); return }
      if (e.key === 'ArrowLeft')  setLbIndex(i => Math.max(0, i - 1))
      if (e.key === 'ArrowRight') setLbIndex(i => Math.min(lbList.length - 1, i + 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lbIndex, lbList.length])

  const clearFilters = () => { setYearFilter(''); setTagFilter(''); setPartFilter('') }

  const filtered = useMemo(() => allMedia.filter(m => {
    if (yearFilter && m.tournament_year !== parseInt(yearFilter)) return false
    if (tagFilter  && !(tagMap[m.id]  || []).includes(parseInt(tagFilter)))  return false
    if (partFilter && !(partMap[m.id] || []).includes(parseInt(partFilter))) return false
    return true
  }), [allMedia, yearFilter, tagFilter, partFilter, tagMap, partMap])

  const filteredPhotos = useMemo(() => filtered.filter(m => m.file_type === 'photo'), [filtered])
  const filteredVideos = useMemo(() => filtered.filter(m => m.file_type === 'video'),  [filtered])
  const photoGroups    = useMemo(() => groupByYear(filteredPhotos), [filteredPhotos])
  const videoGroups    = useMemo(() => groupByYear(filteredVideos), [filteredVideos])
  const availableYears = useMemo(
    () => [...new Set(allMedia.map(m => m.tournament_year))].sort((a, b) => b - a),
    [allMedia]
  )

  const openLb  = (list, idx) => { setLbList(list); setLbIndex(idx) }
  const closeLb = () => setLbIndex(-1)
  const hasFilters = yearFilter || tagFilter || partFilter
  const lbItem  = lbList[lbIndex]

  return (
    <div className="pub-results-page-wrap">
      <PublicNav />

      {/* Filter bar */}
      <div className="pub-gal-filter-bar">
        <div className="pub-gal-filter-inner">
          <div className="pub-gal-filter-controls">
            <select className="pub-gal-select" value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
              <option value="">All Years</option>
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className="pub-gal-select" value={tagFilter} onChange={e => setTagFilter(e.target.value)}>
              <option value="">All Tags</option>
              {tagTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select className="pub-gal-select" value={partFilter} onChange={e => setPartFilter(e.target.value)}>
              <option value="">All Participants</option>
              {participants.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {hasFilters && (
              <button className="pub-gal-clear-btn" onClick={clearFilters}>Clear Filters</button>
            )}
          </div>
          {!loading && (
            <div className="pub-gal-count">
              {filteredPhotos.length} photo{filteredPhotos.length !== 1 ? 's' : ''}
              {filteredVideos.length > 0 && ` · ${filteredVideos.length} video${filteredVideos.length !== 1 ? 's' : ''}`}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="pub-gal-body">
        {loading ? (
          <div className="pub-loading" style={{ padding: '5rem 0', textAlign: 'center' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="pub-gal-empty">
            <p>No photos found for the selected filters.</p>
            <button className="pub-gal-clear-btn" onClick={clearFilters}>Clear Filters</button>
          </div>
        ) : (
          <>
            {photoGroups.map(([year, photos]) => (
              <div key={year} className="pub-gal-year-group">
                <div className="pub-gal-year-label">{year}</div>
                <div className="pub-gal-grid">
                  {photos.map(m => (
                    <div key={m.id} className="pub-gal-tile" onClick={() => openLb(filteredPhotos, filteredPhotos.indexOf(m))}>
                      <img src={m.signedUrl} alt={m.caption || ''} className="pub-gal-tile-img" loading="lazy" />
                      <div className="pub-gal-tile-overlay">
                        {m.caption && <span className="pub-gal-tile-caption">{m.caption}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {videoGroups.length > 0 && (
              <div className="pub-gal-videos-section">
                <div className="pub-gal-videos-heading">Videos</div>
                {videoGroups.map(([year, videos]) => (
                  <div key={year} className="pub-gal-year-group">
                    <div className="pub-gal-year-label">{year}</div>
                    <div className="pub-gal-grid">
                      {videos.map(m => (
                        <div key={m.id} className="pub-gal-tile pub-gal-tile-video" onClick={() => openLb(filteredVideos, filteredVideos.indexOf(m))}>
                          <video src={m.signedUrl} className="pub-gal-tile-img" preload="metadata" muted />
                          <div className="pub-gal-play-btn">&#9654;</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      {lbIndex >= 0 && lbItem && (
        <div className="pub-gal-lb-overlay" onClick={closeLb}>
          <button className="pub-gal-lb-close" onClick={e => { e.stopPropagation(); closeLb() }}>&#10005;</button>
          {lbIndex > 0 && (
            <button className="pub-gal-lb-prev" onClick={e => { e.stopPropagation(); setLbIndex(i => i - 1) }}>&#8249;</button>
          )}
          {lbIndex < lbList.length - 1 && (
            <button className="pub-gal-lb-next" onClick={e => { e.stopPropagation(); setLbIndex(i => i + 1) }}>&#8250;</button>
          )}
          <div className="pub-gal-lb-content" onClick={e => e.stopPropagation()}>
            {lbItem.file_type === 'video'
              ? <video src={lbItem.signedUrl} controls autoPlay className="pub-gal-lb-media" />
              : <img src={lbItem.signedUrl} alt={lbItem.caption || ''} className="pub-gal-lb-media" />
            }
            {lbItem.caption && <div className="pub-gal-lb-caption">{lbItem.caption}</div>}
          </div>
        </div>
      )}
    </div>
  )
}